import { useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAppSelector } from '@store/hooks';
import { selectUser, selectAuthError, selectIsAuthLoading } from '@store/slices';
import { loginRequest } from './msalConfig';
import { telemetry, startInteraction } from '@/services';
import { EVENT_TYPES } from '@/constants';
import type { UseAuthResult } from '@/types/auth.types';

export function useAuth(): UseAuthResult {
  const { instance, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();

  const user = useAppSelector(selectUser);
  const error = useAppSelector(selectAuthError);
  const reduxIsLoading = useAppSelector(selectIsAuthLoading);

  const isLoading = reduxIsLoading || inProgress !== InteractionStatus.None;

  const isAuthenticated = isMsalAuthenticated && user !== null;

  const login = useCallback(async (): Promise<void> => {
    startInteraction();
    telemetry.trackEvent('Auth', EVENT_TYPES.START, { operationName: 'Login' });
    telemetry.trackButtonClick('Auth', 'Login');
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('[Auth] Login redirect failed:', err);
      if (err instanceof Error) {
        telemetry.trackFailure('Auth', 'Login', err.message, {
          errorCode: 'redirect_failed',
        });
        telemetry.trackException(err, 'Auth', { action: 'login' });
      }
    }
  }, [instance]);

  const logout = useCallback(async (): Promise<void> => {
    startInteraction();
    telemetry.trackButtonClick('Auth', 'Logout');
    try {
      const account = instance.getActiveAccount();
      await instance.logoutRedirect({
        account,
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (err) {
      console.error('[Auth] Logout redirect failed:', err);
      if (err instanceof Error) {
        telemetry.trackException(err, 'Auth', { action: 'logout' });
      }
    }
  }, [instance]);

  return {
    isAuthenticated,
    isLoading,
    user,
    error,
    login,
    logout,
  };
}
